'use client'

import { type NiceModalHandler, useModal } from '@ebay/nice-modal-react'
import {
    dispatchBaseModalToast,
    registerBaseModalToastChannel,
    useBaseModalToasts,
} from '@trustoo/components/modal/baseModalToastStore'
import {
    DIALOG_MOBILE_FULL,
    DIALOG_MOBILE_SHEET,
} from '@trustoo/components/modal/constants/dialogPresets'
import Dialog from '@trustoo/components/modal/Dialog'
import { getSheetDialogStatusClassName } from '@trustoo/components/modal/hooks/useDialogTransition'
import ToastList from '@trustoo/components/ui/toaster/ToastList'
import { useCloseOnBack } from '@trustoo/hooks/useCloseOnBack'
import {
    ComponentPropsWithoutRef,
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useMemo,
} from 'react'
import { twMerge } from 'tailwind-merge'

export enum ScrollArea {
    /**
     *  the whole modal scroll
     */
    BODY = 'body',
    /**
     *  only the part in <BaseModalContent> scrolls
     */
    CONTENT = 'content',
}

interface Props {
    /** Close when the backdrop (area outside the card) is clicked. Defaults to `true`. */
    closeOnOutSideClick?: boolean
    /** Full-screen, square-cornered below the `md` breakpoint. */
    mobileFullscreen?: boolean
    /**
     * Bottom-docked sheet below the `md` breakpoint; ignored when `mobileFullscreen` is also set.
     */
    mobileSheet?: boolean
    /** Hide the close cross entirely (e.g. for flows that must be completed). */
    hideCloseButton?: boolean
    /**
     * Blocks the close button, a backdrop click and Escape while true, for a short-lived operation
     * the modal must not be dismissed during. Prefer this over declining inside `onClose`, which
     * strands the modal's Back-history entry — see `useCloseOnBack`. A Back press still closes, so
     * do not rely on this to keep a modal open indefinitely.
     */
    preventClose?: boolean
    /**
     * Custom close handler. When omitted, the modal resolves (with `undefined`) then hides,
     * finally calling `onAfterClose`.
     */
    onClose?: () => void
    /** Fired after the default close handler resolves + hides the modal. */
    onAfterClose?: () => void
    /** Max width of the modal card. A number is treated as pixels. Defaults to 640px. */
    maxWidth?: number | string
    /** Extra classes for the modal card. */
    className?: string
    /** Extra classes for the close button. */
    closeButtonClassName?: string
    /** Accessible name for the icon-only close button, set as its `aria-label`. */
    closeButtonLabel?: string
    /** Where the scrollbar appears within the modal. Defaults to `ScrollArea.BODY`. */
    scrollArea?: ScrollArea
    /** Unique id for the browser-Back overlay stack. Defaults to the NiceModal `id`. */
    overlayId?: string
    /** The dialog's accessible name, for a card without one fixed heading to be named by. */
    'aria-label'?: string
    /** Id of the heading inside the card that names the dialog, when it has one. */
    'aria-labelledby'?: string
}

/**
 * The provider state: the live NiceModal dialog state (`NiceModalHandler`) extended with the
 * modal-specific extras the subcomponents need. Content inside a `BaseModal` reads this via
 * `useBaseModal()`, so it can reach the dialog handler (`resolve`/`hide`/`remove`/`visible`/...)
 * and the Back-aware `close` without prop-drilling.
 *
 * Toasts are deliberately not part of this: `useBaseModalToast()` reaches the card's channel from
 * anywhere within the NiceModal component, including the component rendering this card, which
 * sits above this context.
 */
export interface BaseModalContextValue extends NiceModalHandler {
    scrollArea: ScrollArea
    /** Whether the modal renders its close button; the header reserves room for it. */
    hasCloseButton: boolean
    /** Back-aware close: gives the overlay's history entry back before closing. */
    close: () => void
}

const BaseModalContext = createContext<BaseModalContextValue | null>(null)

/**
 * Reads the surrounding `BaseModal`'s state. Throws when there is no `BaseModal` above, since a
 * caller reaching for `close` or the dialog handler cannot do anything sensible without one.
 */
export const useBaseModal = (): BaseModalContextValue => {
    const context = useContext(BaseModalContext)

    if (!context) {
        throw new Error('useBaseModal must be called inside a <BaseModal>.')
    }

    return context
}

/**
 * The standard modal surface: a `<dialog>` card with an optional header / main content / footer,
 * driven by NiceModal. Render it from a `NiceModal.create(...)` wrapper and open it with
 * `NiceModal.show(...)`; it reads its own visibility from `useModal()` and unmounts itself once the
 * exit animation has finished.
 *
 * The card is the dialog element itself, so it sizes to the viewport and owns the scrollbar — see
 * `scrollArea` for which part of it scrolls.
 *
 * Dismissing with the browser / Android Back button is built in, so the close button, a backdrop
 * click, Escape and Back all close through the same path.
 *
 * The card owns a toast channel keyed by its NiceModal id for as long as it is mounted, rendered
 * over the main content by `BaseModalMainContent`. See `useBaseModalToast`.
 */
const BaseModal = ({
    children,
    className,
    closeOnOutSideClick = true,
    mobileFullscreen = false,
    mobileSheet = false,
    hideCloseButton = false,
    preventClose = false,
    onClose: onCloseProp = undefined,
    onAfterClose,
    scrollArea = ScrollArea.CONTENT,
    maxWidth = 640,
    closeButtonClassName,
    closeButtonLabel,
    overlayId,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
}: PropsWithChildren<Props>) => {
    const modal = useModal()
    const onClose =
        onCloseProp ||
        (() => {
            modal.resolve()
            modal.hide()
            onAfterClose && onAfterClose()
        })

    // Every close goes through this, so the extra history entry is always handed back.
    const { close } = useCloseOnBack({
        isOpen: modal.visible,
        onClose,
        overlayId: overlayId ?? modal.id,
        preventClose,
    })

    useEffect(() => registerBaseModalToastChannel(modal.id), [modal.id])

    const contextValue = useMemo<BaseModalContextValue>(
        () => ({ ...modal, scrollArea, hasCloseButton: !hideCloseButton, close }),
        [modal, scrollArea, hideCloseButton, close],
    )

    return (
        <Dialog.AnimatedRoot
            open={modal.visible}
            onClose={close}
            onExited={() => modal.remove()}
            getStatusClassName={
                mobileSheet && !mobileFullscreen ? getSheetDialogStatusClassName : undefined
            }
            closeOnBackdropClick={closeOnOutSideClick}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            style={{ maxWidth }}
            className={twMerge(
                'flex max-h-[calc(100dvh-32px)] min-h-[130px] w-[calc(100vw-24px)] flex-col bg-[#f7f7f7] [&_label]:h-auto',
                scrollArea === ScrollArea.BODY ? 'overflow-y-auto' : 'overflow-hidden',
                mobileSheet && !mobileFullscreen && DIALOG_MOBILE_SHEET,
                mobileFullscreen && DIALOG_MOBILE_FULL,
                className,
            )}>
            {!hideCloseButton && (
                <Dialog.CloseButton
                    onClick={close}
                    className={closeButtonClassName}
                    aria-label={closeButtonLabel}
                />
            )}
            <BaseModalContext.Provider value={contextValue}>{children}</BaseModalContext.Provider>
        </Dialog.AnimatedRoot>
    )
}

export const BaseModalHeader = ({
    children,
    className,
    ...rest
}: ComponentPropsWithoutRef<'div'>) => {
    const { hasCloseButton } = useBaseModal()
    return (
        <div
            {...rest}
            className={twMerge(
                'flex shrink-0 flex-nowrap items-center justify-between gap-4 px-4 py-3',
                'border-b border-solid border-grey-500',
                // Keep the title clear of the close button, which floats over the top-right corner.
                hasCloseButton && 'pr-11',
                className,
            )}>
            {children}
        </div>
    )
}

export const BaseModalMainContent = ({
    children,
    className,
    ...rest
}: ComponentPropsWithoutRef<'div'>) => {
    const { scrollArea } = useBaseModal()
    return (
        <div
            className={twMerge(
                'relative flex min-h-0 grow flex-col',
                scrollArea === ScrollArea.BODY && 'shrink-0',
            )}>
            <div
                {...rest}
                className={twMerge(
                    'min-h-0 grow p-4',
                    scrollArea === ScrollArea.CONTENT && 'overflow-auto',
                    className,
                )}>
                {children}
            </div>
            <BaseModalToastOutlet />
        </div>
    )
}

/** Only this subscribes to the channel, so a toast re-renders nothing else in the card. */
const BaseModalToastOutlet = () => {
    const { id } = useBaseModal()
    const toasts = useBaseModalToasts(id)
    return (
        <div className="pointer-events-none absolute inset-x-4 inset-y-2 z-10 flex flex-col justify-end">
            <ToastList
                toasts={toasts}
                onToastExited={(toastId) =>
                    dispatchBaseModalToast(id, { type: 'delete', id: toastId })
                }
                // Like the app Toaster: only the toasts catch clicks, the list box passes them
                // through. Wheel scrolling still reaches this list from a toast via scroll chaining.
                className="pointer-events-none max-h-full overflow-y-auto overscroll-contain shrink-0"
                popupClassName="shrink-0 rounded-[5px]"
            />
        </div>
    )
}

export const BaseModalFooter = ({
    children,
    className,
    ...rest
}: ComponentPropsWithoutRef<'div'>) => (
    <div
        {...rest}
        className={twMerge(
            'shrink-0 border-t border-solid border-grey-500 bg-white p-4',
            className,
        )}>
        {children}
    </div>
)

export default BaseModal
